
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus } from 'lucide-react';
import { ChatbotData } from '@/components/chat/chatTypes';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface PersonaFormProps {
  onPersonaAdded: (newChatbot: ChatbotData) => void;
}

const PersonaForm = ({ onPersonaAdded }: PersonaFormProps) => {
  const { toast } = useToast();
  // State for new persona form
  const [newPersona, setNewPersona] = useState<Partial<ChatbotData>>({
    name: '',
    description: '',
    avatarUrl: '',
    tags: []
  });
  const [newTag, setNewTag] = useState('');

  // Handle adding a tag to the new persona
  const handleAddTag = () => {
    if (newTag && !newPersona.tags?.includes(newTag)) {
      setNewPersona({
        ...newPersona,
        tags: [...(newPersona.tags || []), newTag]
      });
      setNewTag('');
    }
  };
  
  // Remove a tag from the new persona
  const handleRemoveTag = (tagToRemove: string) => {
    setNewPersona({
      ...newPersona,
      tags: newPersona.tags?.filter(tag => tag !== tagToRemove)
    });
  };

  // Add a new persona
  const handleAddPersona = async () => {
    if (!newPersona.name || !newPersona.description || !newPersona.avatarUrl) {
      toast({
        title: "Missing Information",
        description: "Please fill all required fields",
        variant: "destructive"
      });
      return;
    }
    
    // Create ID from name (simplified for demo)
    const id = newPersona.name?.toLowerCase().replace(/\s+/g, '-') || '';
    
    try {
      // Insert new agent
      const { data: agentData, error: agentError } = await supabase
        .from('agents')
        .insert({
          id,
          name: newPersona.name || '',
          description: newPersona.description || '',
          tags: newPersona.tags || [],
          avatar_url: newPersona.avatarUrl || '',
          conversations: 0
        })
        .select();
      
      if (agentError) throw agentError;
      
      // Insert default system prompt
      const defaultPrompt = `Default system prompt for ${newPersona.name}`;
      const { error: promptError } = await supabase
        .from('system_prompts')
        .insert({
          agent_id: id,
          content: defaultPrompt
        });
      
      if (promptError) throw promptError;
      
      // Create new chatbot object for parent component
      const newChatbot: ChatbotData = {
        id,
        name: newPersona.name || '',
        description: newPersona.description || '',
        tags: newPersona.tags || [],
        avatarUrl: newPersona.avatarUrl || '',
        conversations: 0
      };
      
      // Notify parent component
      onPersonaAdded(newChatbot);
      
      // Reset form
      setNewPersona({
        name: '',
        description: '',
        avatarUrl: '',
        tags: []
      });
      
      toast({
        title: "Persona Added",
        description: "New chatbot persona has been created"
      });
    } catch (error) {
      console.error('Error adding persona:', error);
      toast({
        title: "Error",
        description: "Failed to add new persona",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add New Persona</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Name</label>
            <Input 
              placeholder="e.g. Abraham Lincoln" 
              value={newPersona.name}
              onChange={(e) => setNewPersona({...newPersona, name: e.target.value})}
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Avatar URL</label>
            <Input 
              placeholder="Image URL" 
              value={newPersona.avatarUrl}
              onChange={(e) => setNewPersona({...newPersona, avatarUrl: e.target.value})}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">Description</label>
          <Textarea 
            placeholder="Brief description of the persona..." 
            value={newPersona.description}
            onChange={(e) => setNewPersona({...newPersona, description: e.target.value})}
          />
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">Tags</label>
          <div className="flex gap-2 mb-2">
            <Input 
              placeholder="Add a tag" 
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
            />
            <Button type="button" onClick={handleAddTag}>Add</Button>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {newPersona.tags?.map(tag => (
              <Badge 
                key={tag} 
                variant="secondary"
                className="flex gap-1 items-center"
              >
                {tag}
                <button 
                  className="ml-1 text-xs hover:text-destructive" 
                  onClick={() => handleRemoveTag(tag)}
                >
                  &times;
                </button>
              </Badge>
            ))}
          </div>
        </div>
        
        <Button className="w-full md:w-auto" onClick={handleAddPersona}>
          <Plus size={16} className="mr-2" />
          Add Persona
        </Button>
      </CardContent>
    </Card>
  );
};

export default PersonaForm;
